// 暫定距離の取得（キャッシュ優先）。純ロジックは routeDistance.core.js、ここは配線だけ。
//
// キャッシュの持ち方: settings/routecache 1ドキュメント {pairs:{'pref+city|pref+city':{km,at}}}。
// settings/labor・settings/display と同じ settings 行の流儀（新テーブル・マイグレーション無し）。
// 想定ペア数は数百のオーダーなので1ドキュメントで足りる。
//
// 読み書きの置き場所の決め: **クライアント側（authed anon）**。理由:
//  1. settings の読み書きは saveLaborSettings 等と同じくクライアント（authed）で行うのが既存の流儀
//  2. service role を使うのは独立 Workers（email-intake / extract）だけで、functions/ 内の参照は0件。
//     キャッシュのために Pages Function へ特権キーの利用を持ち込まない方が安全（漏洩面を増やさない）
//  3. キャッシュヒット時は Function すら呼ばずに済む＝無料枠防衛・応答も速い
// 設計: engineering-タクミ/docs/2026-08-28-暫定距離-設計.md
import { COLLECTIONS, getOne, upsert } from './collections.js'
import { supabaseReady } from '../supabase.js'
import { accessToken } from '../serverData.js'
import { createRouteKmGetter } from './routeDistance.core.js'

async function loadPairs() {
  if (!supabaseReady) return null
  const doc = await getOne(COLLECTIONS.settings, 'routecache')
  return (doc && doc.pairs) || null
}

// 読み直してからマージ保存（upsert はトップレベルマージなので pairs は自分で合成する）。
// 同時保存で他セッションの1件が落ちる可能性はあるが、キャッシュなので次回の取得で埋め直る。
// 値は {km, min, at}（min＝所要時間・分。2026-08-28 追加）。旧形式（km のみ）の既存ペアはそのまま＝壊さない。
async function savePair(key, val) {
  if (!supabaseReady) return
  const doc = await getOne(COLLECTIONS.settings, 'routecache').catch(() => null)
  const entry = { km: val.km, ...(val.min > 0 ? { min: val.min } : {}), at: new Date().toISOString() }
  const pairs = { ...((doc && doc.pairs) || {}), [key]: entry }
  await upsert(COLLECTIONS.settings, 'routecache', { pairs, updatedAt: new Date().toISOString() })
}

// Pages Function 経由で Google Routes API を呼ぶ（キーはサーバ側のみ）。失敗は理由つき Error。
async function fetchRoute(origin, dest) {
  const token = await accessToken()
  if (!token) throw new Error('サインインしてください')
  const base = import.meta.env.BASE_URL || '/'
  const res = await fetch(`${base}api/route-distance`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ origin, dest }),
  })
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error((data && data.error) || `距離の取得に失敗しました（${res.status}）`)
  return { km: data && data.km, min: data && data.min }
}

// getRouteKm(origin, dest) → {km, min, source:'cache'|'api'}。メモリ→settings/routecache→API の順。
export const getRouteKm = createRouteKmGetter({ loadPairs, savePair, fetchRoute })
